import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// PACKAGES
import { useParams } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { useHistory } from 'react-router-dom';

// COMPONENTS
import StepOne from './StepOne';
import StepTwo from './StepTwo';
import StepThree from './StepThree';
import StepFour from './StepFour';
import LoadingSpinner from '../../components/LoadingSpinner';
import Button from '../../components/Button';
import GlobalModal from '../../components/GlobalModal';
import ExitConfirmationModal from './Modals/ExitConfirmationModal';

// ICONS
import { ReactComponent as ArrowLeftIcon } from '../../assets/svg/chevron-left.svg';
import { ReactComponent as ArrowRightIcon } from '../../assets/svg/chevron-right.svg';
import { ReactComponent as ArrowLeft } from '../../assets/svg/arrow-left.svg';

// TYPES
import { IncidentEditUser, IncidentEditCommand, IncidentEditAttachment } from '../../api/models';
import { RouteParams } from '../../Interfaces';
import { FileType } from '../../components/FileUpload';

export interface IncidentFormTypes extends IncidentEditCommand {
	newAttachments: FileType[];
}

const IncidentSubmit: React.FC = () => {
	const history = useHistory();

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// get ID from params
	const params = useParams<RouteParams>();
	const incidentId = params.id;

	// Set up form state and initial form data
	const initialIncidentFormData: IncidentFormTypes = {
		// STEP ONE
		name: '',
		age: '',
		dateOfBirth: '',
		title: '',
		occurrence: '',
		type: undefined,
		specification: '',
		location: '',
		description: '',
		witness: false,
		witnesses: [],

		// STEP TWO
		firstAidAdministered: false,
		firstAid: '',
		medicationAdministered: false,
		medication: [],
		medicalPersonnelContacted: false,
		medicalPersonnel: [],
		emergencyServiceContacted: false,
		emergencyServices: [],
		takenToHospital: false,
		hospital: '',
		dateAndTimeTakenToHospital: '',
		personTakenToHospital: '',
		relationshipToChild: '',
		otherActions: false,
		otherActionsTaken: '',

		// STEP THREE
		individualsNotified: false,
		individuals: [],
		authorityNotified: false,
		authorities: [],
		notes: '',
		newAttachments: [],
		createAttachments: [],
		attachments: [],
		deleteAttachmentIds: [],

		leaderRecipientIds: [],
		otherRecipientIds: [],

		otherRecipientEmails: [],

		otherRecipients: [],
		leaders: [],

		// STEP FOUR:
		reviewer: '',
		reviewerRole: ''
	};

	const [incidentFormData, setIncidentFormData] = React.useState(initialIncidentFormData);

	// If there is an id, then load Edit screen, otherwise load blank to create new
	const loadIncidentReport = async () => {
		setIsLoading(true);

		if (incidentId) {
			const response = await apibridge.loadIncidentReport(incidentId);

			if (response.isError) {
				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				const { result = initialIncidentFormData } = response;

				setIncidentFormData(result as IncidentFormTypes);
			}
		} else {
			const response = await apibridge.loadIncidentReport();

			if (response.isError) {
				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				const { result = initialIncidentFormData } = response;

				setIncidentFormData({
					...initialIncidentFormData,
					leaders: result.leaders
				});
			}
		}

		setIsLoading(false);
	};

	React.useEffect(() => {
		loadIncidentReport();
	}, []);

	const [leadershipTeamData, setLeadershipTeamData] = React.useState<IncidentEditUser[]>([]);

	React.useEffect(() => {
		setLeadershipTeamData(incidentFormData.leaders || []);
	}, [incidentFormData.leaders]);

	const [otherRecipientsData, setOtherRecipientsData] = React.useState<IncidentEditUser[]>([]);

	React.useEffect(() => {
		setOtherRecipientsData(incidentFormData.otherRecipients || []);
	}, [incidentFormData.otherRecipients]);

	// State to manage Modal
	const [showExitConfirmationModal, setShowExitConfirmationModal] = React.useState<boolean>(false);

	// State to control active step
	const [activeStep, setActiveStep] = React.useState('step1');

	// Logic for Back Button
	const mobileStepHandler = () => {
		if (activeStep === 'step4') {
			setActiveStep('step3');
		}

		if (activeStep === 'step3') {
			setActiveStep('step2');
		}

		if (activeStep === 'step2') {
			setActiveStep('step1');
		}

		if (activeStep === 'step1') {
			setShowExitConfirmationModal(true);
		}
	};

	React.useEffect(() => {
		const pageState = history.location.state;

		if (pageState === undefined || pageState.activeStep === undefined || pageState.activeStep === null) {
			history.replace(window.location.pathname, { activeStep });
		} else if (pageState.activeStep !== activeStep) {
			history.push(window.location.pathname, { activeStep });
		}

		let unlisten = history.listen((location, action) => {
			if (
				action === 'POP' &&
				location.state &&
				location.state.activeStep !== undefined &&
				location.state.activeStep !== null
			) {
				setActiveStep(location.state.activeStep);
			}
		});

		return unlisten;
	}, [activeStep]);

	const goBack = () => {
		mobileStepHandler();
	};

	return (
		<div className="page-general-layout">
			<div className="page-heading">
				<Button className="btn-text-icon" onClick={goBack} buttonIcon={<ArrowLeft className="link-icon" />}>
					{activeStep === 'step1' ? 'Back to dashboard' : 'Back'}
				</Button>

				<h1 className="h1">Submit an incident report</h1>
			</div>

			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<div className="form-steps-controller-desktop d-none d-lg-flex">
									<div
										className={`steps-name ${
											activeStep === 'step1' ||
											activeStep === 'step2' ||
											activeStep === 'step3' ||
											activeStep === 'step4'
												? 'active'
												: ''
										}`}
										onClick={() => setActiveStep('step1')}
									>
										1. Incident details
									</div>

									<ArrowRightIcon className="step-icon" />

									<div
										className={`steps-name ${
											activeStep === 'step2' || activeStep === 'step3' || activeStep === 'step4' ? 'active' : ''
										}`}
										onClick={() => setActiveStep('step2')}
									>
										2. Actions taken
									</div>

									<ArrowRightIcon className="step-icon" />

									<div
										className={`steps-name ${activeStep === 'step3' || activeStep === 'step4' ? 'active' : ''}`}
										onClick={() => setActiveStep('step3')}
									>
										3. Notification
									</div>

									<ArrowRightIcon className="step-icon" />

									<div className={`steps-name ${activeStep === 'step4' ? 'active' : ''}`}>4. Review & Submit</div>
								</div>

								<div className="form-steps-controller-mobile d-flex d-lg-none">
									<div className="steps-name-group">
										{activeStep === 'step1' && <div className="steps-name active">1. Incident details</div>}
										{activeStep === 'step2' && <div className="steps-name active">2. Actions taken</div>}
										{activeStep === 'step3' && <div className="steps-name active">3. Notification</div>}
										{activeStep === 'step3' && <div className="steps-name active">3. Review & Submit</div>}
									</div>
									<div className="controller-group">
										<div
											className={`controller-wrapper ${
												activeStep === 'step2' || activeStep === 'step3' || activeStep === 'step4' ? 'active' : ''
											}`}
											onClick={mobileStepHandler}
										>
											<ArrowLeftIcon className="step-controller" />
										</div>
										<div className="controller-wrapper">
											<ArrowRightIcon className="step-controller" />
										</div>
									</div>
								</div>

								<div className="general-divider"></div>

								{activeStep === 'step1' && (
									<StepOne
										incidentFormData={incidentFormData}
										setIncidentFormData={setIncidentFormData}
										setActiveStep={setActiveStep}
									/>
								)}

								{activeStep === 'step2' && (
									<StepTwo
										incidentFormData={incidentFormData}
										setIncidentFormData={setIncidentFormData}
										setActiveStep={setActiveStep}
									/>
								)}

								{activeStep === 'step3' && (
									<StepThree
										incidentFormData={incidentFormData}
										setIncidentFormData={setIncidentFormData}
										setActiveStep={setActiveStep}
										leadershipTeamData={leadershipTeamData}
										otherRecipientsData={otherRecipientsData}
										setOtherRecipientsData={setOtherRecipientsData}
									/>
								)}

								{activeStep === 'step4' && (
									<StepFour
										incidentFormData={incidentFormData}
										setIncidentFormData={setIncidentFormData}
										setActiveStep={setActiveStep}
										leadershipTeamData={leadershipTeamData}
										otherRecipientsData={otherRecipientsData}
										isLoading={isLoading}
										setIsLoading={setIsLoading}
									/>
								)}

								<GlobalModal
									isOpen={showExitConfirmationModal}
									setIsOpen={setShowExitConfirmationModal}
									titleText="Cancel incident report"
								>
									<ExitConfirmationModal />
								</GlobalModal>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default IncidentSubmit;
